import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { HOME_MENU_ITEMS } from '../utils'

interface HomeMenuPopoverProps {
  isOpen: boolean
  onClose: () => void
  buttonRef: React.RefObject<HTMLButtonElement | null>
}

export const HomeMenuPopover: React.FC<HomeMenuPopoverProps> = ({ isOpen, onClose, buttonRef }) => {
  const popoverRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  const handleMenuClick = (href: string) => {
    window.location.href = href
    onClose()
  }

  // Calculate position based on button position
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect()

      setPosition({
        top: buttonRect.bottom + 15,
        left: buttonRect.right - 168, // Align right edge of popover with right edge of button
      })
    }
  }, [isOpen, buttonRef])

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const clickedButton = buttonRef.current?.contains(target)
      const clickedPopover = popoverRef.current?.contains(target)

      if (!clickedButton && !clickedPopover) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose, buttonRef])

  if (!isOpen) return null

  // Portal content
  const popoverContent = (
    <div
      ref={popoverRef}
      className="bg-white rounded-lg shadow-lg border border-gray-200 py-2 fixed"
      style={{
        width: '168px',
        top: `${position.top}px`,
        left: `${position.left}px`,
        borderRadius: '15px',
        zIndex: 999999,
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
      }}
    >
      <div className="py-1">
        {HOME_MENU_ITEMS.map((item) => (
          <button
            key={item.id}
            onClick={() => handleMenuClick(item.href)}
            className="w-full px-4 py-2 flex items-center hover:bg-gray-50 transition-colors text-left"
          >
            <span className="text-sm text-gray-900">{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  )

  // Render using Portal to escape parent stacking context
  return createPortal(popoverContent, document.body)
}
